import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import DOMPurify from "dompurify";
import { makeRepertoPin, makeMyMapsPin } from "./SwissTopoMap";
import "../lib/map.css";

/**
 * Italy map — built on top of OpenStreetMap and community providers
 * because Italy has no single national portal equivalent to SwissTopo
 * or ARSO that is free / CORS-friendly.
 *
 * Three selectable base layers (radio):
 *   1. STANDARD — `tile.openstreetmap.org`  (default ON)
 *   2. CYCLOSM  — `tile-cyclosm.openstreetmap.fr`  (bike-oriented, lovely terrain shading)
 *   3. OPENTOPOMAP — `tile.opentopomap.org`  (topographic with contours & hiking trails;
 *      free Tracestrack-style alternative)
 *
 * All providers serve plain Web-Mercator tiles, so this component is the
 * cleanest of the three country maps — just a `TileLayer` per provider.
 */

const ITALY_CENTER = [42.5, 12.5];
const ITALY_DEFAULT_ZOOM = 6;

const OSM_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>';

const BASE_LAYERS = [
  {
    id: "standard",
    label: "Standard",
    url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: OSM_ATTR,
    maxZoom: 19,
    default: true,
  },
  {
    id: "cyclosm",
    label: "CyclOSM",
    url: "https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png",
    attribution: `${OSM_ATTR} | <a href="https://www.cyclosm.org" target="_blank" rel="noreferrer">CyclOSM</a>`,
    subdomains: ["a", "b", "c"],
    maxZoom: 20,
  },
  {
    id: "opentopo",
    label: "OpenTopoMap (rilievo)",
    url: "https://tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: `${OSM_ATTR} | <a href="https://opentopomap.org" target="_blank" rel="noreferrer">OpenTopoMap</a>`,
    maxZoom: 17,
  },
];

// ---------------------------------------------------------------
// Layer switcher — identical visual language to Swiss/Slovenia
// (shared `.layer-switcher` CSS class → consistent UI across maps).
// ---------------------------------------------------------------
const LayerSwitcher = ({ baseId, setBaseId }) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className={`layer-switcher${collapsed ? " collapsed" : ""}`} data-testid="layer-switcher">
      <button
        type="button"
        className="switcher-toggle"
        onClick={() => setCollapsed((v) => !v)}
        data-testid="layer-switcher-toggle"
      >
        <span>Strati</span>
        <span className="toggle-icon">▾</span>
      </button>
      <div className="switcher-body">
        <div className="group-title">Sfondo</div>
        {BASE_LAYERS.map((l) => (
          <div key={l.id} className="layer-row">
            <label>
              <input
                type="radio"
                name="it-base"
                checked={baseId === l.id}
                onChange={() => setBaseId(l.id)}
                data-testid={`it-base-${l.id}`}
              />
              <span>{l.label}</span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

const ClickHandler = ({ enabled, onPick }) => {
  useMapEvents({
    click(e) {
      if (enabled && onPick) onPick(e.latlng);
    },
  });
  return null;
};

const FlyTo = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || 14, { duration: 0.8 });
    // eslint-disable-next-line
  }, [position?.[0], position?.[1]]);
  return null;
};

// ---------------------------------------------------------------
// Main component — prop-compatible with SwissTopoMap/SloveniaMap
// so the country selector in MapPage can swap any of the three
// without other changes.
// ---------------------------------------------------------------
export default function ItalyMap({
  center = ITALY_CENTER,
  zoom = ITALY_DEFAULT_ZOOM,
  markers = [],
  myMapsPoints = [],
  height = 460,
  showLayerSwitcher = true,
  pickable = false,
  onPick,
  pickedPosition = null,
  flyTo = null,
}) {
  const [baseId, setBaseId] = useState(
    BASE_LAYERS.find((l) => l.default).id,
  );
  const base = BASE_LAYERS.find((l) => l.id === baseId) || BASE_LAYERS[0];

  return (
    <div className="relative" style={{ height }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          key={base.id}
          url={base.url}
          attribution={base.attribution}
          subdomains={base.subdomains || ["a", "b", "c"]}
          maxZoom={base.maxZoom || 19}
        />

        <ClickHandler enabled={pickable} onPick={onPick} />
        {flyTo && <FlyTo position={flyTo} zoom={14} />}

        {markers.map((m, i) => (
          <Marker
            key={`${m.code || "m"}-${m.position[0]}-${m.position[1]}-${i}`}
            position={m.position}
            icon={m.code ? makeRepertoPin(m.code) : undefined}
            eventHandlers={m.onClick ? { click: m.onClick } : undefined}
          >
            {m.popup && <Popup>{m.popup}</Popup>}
          </Marker>
        ))}

        {myMapsPoints.map((p, i) => (
          <Marker
            key={`mm-${p.point_id || p.layer_id || "x"}-${p.lat}-${p.lng}-${i}`}
            position={[p.lat, p.lng]}
            icon={makeMyMapsPin(p.color || "#9C5B42")}
          >
            <Popup>
              <div className="min-w-[180px]">
                {p.layer_name && (
                  <div className="font-mono text-[10px] tracking-wider uppercase text-[#9C5B42] mb-1">
                    {p.layer_name}
                  </div>
                )}
                <div className="font-serif text-base text-[#1E2520]">{p.name || "Punto"}</div>
                {p.description && (
                  <div className="text-xs text-[#5C635A] mt-1 whitespace-pre-wrap">
                    {DOMPurify.sanitize(p.description, { ALLOWED_TAGS: [] })}
                  </div>
                )}
                {p.is_personal && (p.onEdit || p.onDelete) && (
                  <div className="flex gap-1.5 mt-2.5 pt-2.5 border-t border-[#E5E0D8]">
                    {p.onEdit && (
                      <button
                        type="button"
                        onClick={p.onEdit}
                        className="text-[11px] text-[#9C5B42] hover:underline"
                        data-testid="mymaps-popup-edit"
                      >
                        Modifica
                      </button>
                    )}
                    {p.onDelete && (
                      <button
                        type="button"
                        onClick={p.onDelete}
                        className="text-[11px] text-red-700 hover:underline ml-auto"
                        data-testid="mymaps-popup-delete"
                      >
                        Elimina
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {pickedPosition && <Marker position={pickedPosition} />}
      </MapContainer>

      {showLayerSwitcher && <LayerSwitcher baseId={baseId} setBaseId={setBaseId} />}
    </div>
  );
}
